import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  House, UsersThree, WifiHigh, Pulse, BellRinging,
  Gear, UserCircle, X, DoorOpen, ShieldCheck, Buildings,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useOrg } from '../contexts/OrgContext';
import TopBar from './TopBar';

const NavItem = ({ to, icon: Icon, label, testid, end }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    className={({ isActive }) =>
      `group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
        isActive
          ? 'bg-sage-100 text-sage-700 font-medium'
          : 'text-ink-soft hover:bg-bg-alt hover:text-ink'
      }`
    }
  >
    <Icon size={18} weight="duotone" />
    <span className="text-sm">{label}</span>
  </NavLink>
);

export default function Layout({ children }) {
  const { user } = useAuth();
  const { t, setLang } = useI18n();
  const { orgVersion } = useOrg();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isFamily = user?.role === 'family';

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // One language setting for everything: once authenticated, the user's saved
  // language (backend source of truth) drives the UI too.
  useEffect(() => {
    if (user?.language) setLang(user.language);
  }, [user?.language, setLang]);

  const Sidebar = (
    <>
      <div className="px-5 py-6 flex items-center justify-between border-b border-stone_line">
        <div className="flex items-center gap-2.5">
          <img
            src="/favicon.png"
            alt={t('brand')}
            data-testid="sidebar-logo"
            className="w-9 h-9 rounded-xl bg-sage-100 p-1 shadow-sm object-contain"
          />
          <div>
            <div className="font-heading text-lg text-ink leading-none">{t('brand')}</div>
            <div className="text-[10px] text-ink-muted mt-1 uppercase tracking-[0.18em]">{t('tagline')}</div>
          </div>
        </div>
        <button
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-close-mobile"
          className="lg:hidden p-2 rounded-lg text-ink-soft hover:bg-bg-alt"
        >
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        <NavItem to="/" end icon={House} label={t('nav_dashboard')} testid="nav-dashboard" />
        <NavItem to="/patients" icon={UsersThree} label={t('nav_patients')} testid="nav-patients" />
        {!isFamily && (
          <NavItem to="/rooms" icon={DoorOpen} label={t('nav_rooms')} testid="nav-rooms" />
        )}
        {!isFamily && (
          <NavItem to="/buildings" icon={Buildings} label={t('nav_buildings')} testid="nav-buildings" />
        )}
        <NavItem to="/devices" icon={WifiHigh} label={t('nav_devices')} testid="nav-devices" />
        <NavItem to="/events" icon={Pulse} label={t('nav_events')} testid="nav-events" />
        <NavItem to="/alerts" icon={BellRinging} label={t('nav_alerts')} testid="nav-alerts" />
        {user?.role === 'admin' && (
          <NavItem to="/team" icon={UserCircle} label={t('nav_users')} testid="nav-team" />
        )}
        {user?.role === 'admin' && (
          <NavItem to="/admin" icon={ShieldCheck} label={t('nav_admin')} testid="nav-admin" />
        )}
        <NavItem to="/settings" icon={Gear} label={t('nav_settings')} testid="nav-settings" />
      </nav>

      <div className="px-5 py-4 border-t border-stone_line text-[10px] text-ink-muted uppercase tracking-[0.18em]">
        v1 · Phase 1
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-bg">
      {/* Desktop sidebar */}
      <aside
        className="w-[240px] shrink-0 border-r border-stone_line bg-white/70 backdrop-blur-sm hidden lg:flex flex-col"
        data-testid="desktop-sidebar"
      >
        {Sidebar}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-drawer-overlay"
        />
      )}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-[280px] bg-white border-r border-stone_line flex flex-col transform transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-drawer"
      >
        {Sidebar}
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <TopBar onOpenMobileMenu={() => setMobileOpen(true)} />
        <div key={orgVersion} className="flex-1 max-w-[1400px] mx-auto w-full px-5 sm:px-8 py-8 lg:py-10">
          {children}
        </div>
      </main>
    </div>
  );
}
